import BlurredImg from '../common/BlurredImg';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, ChevronRight, Minus, Trophy } from 'lucide-react';
import proRankingService, {
  ProRanking,
  ProRankingEntry,
  ProRankingSummary,
} from '../../services/proRankingService';
import PlayerLink from '../PlayerLink';
import { t } from '../../i18n';

const ACCENT = '#3fced1';

/** Wie viele Einträge pro Rangliste angezeigt werden (Top 8). */
export const SIDEBAR_RANKING_TOP_COUNT = 8;
/** Maximal so viele Ranglisten als Umschalt-Pills anbieten. */
export const SIDEBAR_RANKING_MAX_TABS = 3;

/** Eine geladene Rangliste inkl. ihrer Top-Einträge (Fetch macht die Seite). */
export interface SidebarRankingData {
  ranking: ProRanking;
  summary: ProRankingSummary | null;
  entries: ProRankingEntry[];
}

interface SidebarRankingsProps {
  /** Bereits geladene Ranglisten (sortiert); leeres Array → Widget rendert null. */
  rankings: SidebarRankingData[];
}

/** Initialen aus dem Spielernamen ("Luke Littler" → "LL") */
const playerInitials = (name?: string | null): string => {
  if (!name || !name.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase() || '?';
};

/** Spieler-Avatar mit Initialen-Fallback (playerImage aus dem KI-Sync). */
const RankAvatar: React.FC<{ entry: ProRankingEntry; size: 'sm' | 'lg' }> = ({ entry, size }) => {
  const [failed, setFailed] = useState(false);
  const image = proRankingService.buildFileUrl(entry.playerImage);
  const sizeCls = size === 'lg' ? 'h-11 w-11 text-xs' : 'h-6 w-6 text-[8px]';
  return (
    <div
      className={`${sizeCls} shrink-0 overflow-hidden rounded-full border border-zinc-700/60 bg-zinc-800 flex items-center justify-center ${
        size === 'lg' ? 'ring-2 ring-[#3fced1]/60' : ''
      }`}
    >
      {image && !failed ? (
        <img
          src={image}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="select-none font-semibold leading-none text-gray-400">
          {playerInitials(entry.playerName)}
        </span>
      )}
    </div>
  );
};

/**
 * Auf-/Abstiegs-Pfeil aus previousRank vs. rank (Semantik eindeutig, im
 * Gegensatz zum Vorzeichen von rankChange). Ohne Vergleichswert: neutral.
 */
const MovementIndicator: React.FC<{ entry: ProRankingEntry }> = ({ entry }) => {
  const rank = typeof entry.rank === 'number' ? entry.rank : null;
  const prev = typeof entry.previousRank === 'number' ? entry.previousRank : null;
  if (rank === null || prev === null || prev === rank) {
    return <Minus size={10} className="shrink-0 text-zinc-600" />;
  }
  if (prev > rank) {
    return <ArrowUp size={11} className="shrink-0 text-emerald-500" />;
  }
  return <ArrowDown size={11} className="shrink-0 text-red-500" />;
};

/**
 * Sidebar-Widget "Ranglisten" im Portal-Look: KI-Banner der Rangliste als
 * Header (thumbnail/heroImage aus dem Sync, Fallback: Akzent-Verlauf),
 * Platz 1 als Spotlight mit großem Avatar, Plätze 2–8 als dichte Liste mit
 * Avataren und Auf-/Abstiegs-Pfeilen. Bei mehreren Ranglisten schalten
 * kompakte Pills um. Daten kommen als Props von der Seite (kein eigener
 * Fetch → das Widget kann per hidden/lg:hidden doppelt gemountet werden).
 */
const SidebarRankings: React.FC<SidebarRankingsProps> = ({ rankings }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [bannerFailed, setBannerFailed] = useState(false);

  const tabs = rankings
    .filter((r) => r && r.ranking && r.entries.length > 0)
    .slice(0, SIDEBAR_RANKING_MAX_TABS);
  if (tabs.length === 0) return null;

  const active = tabs[Math.min(activeIdx, tabs.length - 1)];
  const { ranking, summary } = active;
  const entries = active.entries.slice(0, SIDEBAR_RANKING_TOP_COUNT);
  const [leader, ...rest] = entries;

  // Im Banner den VOLLEN Namen zeigen (shortName wie „OOM" nur in den Pills)
  const title = ranking.name || ranking.shortName || t('web.component.sidebar-rankings.order-of-merit');
  const listHref = ranking.slug ? `/ranglisten/${ranking.slug}` : '/ranglisten';
  const banner = proRankingService.buildFileUrl(
    ranking.thumbnail ?? ranking.heroImage ?? ranking.image ?? null
  );
  const valueLabel = ranking.valueLabel || summary?.valueLabel || null;

  return (
    <section className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900">
      {/* Header: KI-Banner mit Verlauf, Fallback Akzent-Gradient */}
      <div className="relative h-24">
        {banner && !bannerFailed ? (
          <BlurredImg
            src={banner}
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
            onError={() => setBannerFailed(true)}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#3fced1]/25 via-zinc-900 to-zinc-900">
            <Trophy
              size={72}
              className="absolute -bottom-3 -right-2 rotate-12 text-[#3fced1]/10"
            />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/95 via-zinc-950/40 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 px-4 pb-2.5">
          {ranking.organization && (
            <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: ACCENT }}>
              {ranking.organization}
            </p>
          )}
          <h2 className="section-heading truncate text-base font-bold text-white drop-shadow">
            {title}
          </h2>
        </div>
      </div>

      {/* Umschalt-Pills bei mehreren Ranglisten */}
      {tabs.length > 1 && (
        <div className="flex flex-wrap gap-1.5 border-b border-zinc-800 px-4 py-2.5" role="tablist" aria-label={t('web.component.sidebar-rankings.rangliste-waehlen')}>
          {tabs.map((tab, idx) => {
            const isActive = idx === Math.min(activeIdx, tabs.length - 1);
            const label = tab.ranking.shortName || tab.ranking.name || t('web.component.sidebar-rankings.rangliste', { idx: idx + 1 });
            return (
              <button
                key={tab.ranking._id || tab.ranking.id || idx}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => {
                  setActiveIdx(idx);
                  setBannerFailed(false);
                }}
                className={`max-w-full truncate rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${
                  isActive
                    ? 'border-[#3fced1]/50 bg-[#3fced1]/10'
                    : 'border-zinc-700 text-gray-400 hover:border-zinc-500 hover:text-white'
                }`}
                style={isActive ? { color: ACCENT } : undefined}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Platz 1: Spotlight — Wert rechtsbündig wie in den übrigen Zeilen */}
      {leader && (
        <div className="flex items-center gap-3 border-b border-zinc-800 bg-[#3fced1]/[0.06] px-4 py-3">
          <span className="w-5 shrink-0 text-right text-lg font-black tabular-nums" style={{ color: ACCENT }}>
            1
          </span>
          <RankAvatar entry={leader} size="lg" />
          <PlayerLink
            name={leader.playerName}
            className="min-w-0 flex-1 truncate text-sm font-bold text-white"
          >
            {leader.playerName || t('web.component.comment-item.unbekannt')}
          </PlayerLink>
          <MovementIndicator entry={leader} />
          <div className="flex shrink-0 flex-col items-end">
            <span className="text-sm font-bold tabular-nums" style={{ color: ACCENT }}>
              {proRankingService.formatValue(leader, summary)}
            </span>
            {valueLabel && (
              <span className="text-[10px] font-medium text-gray-500">{valueLabel}</span>
            )}
          </div>
        </div>
      )}

      {/* Plätze 2–8 */}
      <div>
        {rest.map((entry, idx) => (
          <div
            key={entry._id || entry.id || idx}
            className="flex items-center gap-2.5 border-b border-zinc-800/60 px-4 py-[7px] transition-colors last:border-b-0 hover:bg-zinc-800/40"
          >
            <span className="w-5 shrink-0 text-right text-xs font-bold tabular-nums text-gray-500">
              {entry.rank ?? idx + 2}
            </span>
            <MovementIndicator entry={entry} />
            <RankAvatar entry={entry} size="sm" />
            <PlayerLink
              name={entry.playerName}
              className="min-w-0 flex-1 truncate text-sm font-medium text-white"
            >
              {entry.playerName || t('web.component.comment-item.unbekannt')}
            </PlayerLink>
            <span className="shrink-0 text-xs font-semibold tabular-nums text-gray-400">
              {proRankingService.formatValue(entry, summary)}
            </span>
          </div>
        ))}
      </div>

      <Link
        to={listHref}
        className="flex items-center justify-center gap-1 border-t border-zinc-800 px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-zinc-800/40"
        style={{ color: ACCENT }}
      >
        {t('web.component.sidebar-rankings.komplette-rangliste')}{' '}<ChevronRight size={14} />
      </Link>
    </section>
  );
};

export default SidebarRankings;
